import { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { auth, googleProvider, hasFirebaseConfig } from "../firebaseClient";
import {
  getGroceryItems,
  isCloudBacked,
  loadCart,
  saveCart,
} from "../services/groceryStore";
import "./Grocery.css";

function GroceryAuthPanel({
  user,
  isSigningIn,
  authFeedback,
  onLogin,
  onLogout,
  isLogoutDisabled,
}) {
  if (user) {
    return (
      <section className="grocery-auth-inline" aria-live="polite">
        <p className="grocery-auth-email">
          Signed in as <strong>{user.email}</strong>
        </p>
        <p className="grocery-auth-ok">Cart is saved to your account.</p>
        <button
          type="button"
          className="grocery-link-subtle"
          onClick={onLogout}
          disabled={isLogoutDisabled}
          title={isLogoutDisabled ? "Save cart before logout" : ""}
        >
          Logout
        </button>
        {authFeedback ? (
          <p className="grocery-auth-feedback">{authFeedback}</p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="grocery-auth-inline" aria-live="polite">
      <p className="grocery-auth-prompt">
        Sign in to save your cart across devices.
      </p>
      <button
        type="button"
        className="grocery-link-button grocery-auth-cta"
        onClick={onLogin}
        disabled={isSigningIn}
      >
        {isSigningIn ? "Signing in..." : "Sign in with Google"}
      </button>
      {authFeedback ? (
        <p className="grocery-auth-feedback">{authFeedback}</p>
      ) : null}
    </section>
  );
}

GroceryAuthPanel.propTypes = {
  user: PropTypes.shape({ email: PropTypes.string }),
  isSigningIn: PropTypes.bool.isRequired,
  authFeedback: PropTypes.string.isRequired,
  onLogin: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  isLogoutDisabled: PropTypes.bool.isRequired,
};

GroceryAuthPanel.defaultProps = {
  user: null,
};

const scoreItemAgainstQuery = (itemName, query) => {
  if (!query) {
    return 1;
  }

  const name = itemName.toLowerCase();

  if (name === query) {
    return 120;
  }

  if (name.startsWith(query)) {
    return 100 - (name.length - query.length) * 0.2;
  }

  const containsIndex = name.indexOf(query);
  if (containsIndex >= 0) {
    return 80 - containsIndex;
  }

  let qIdx = 0;
  for (let i = 0; i < name.length && qIdx < query.length; i += 1) {
    if (name[i] === query[qIdx]) {
      qIdx += 1;
    }
  }

  if (qIdx === query.length) {
    return 50;
  }

  return 0;
};

function GroceryCartAside({
  cartUpgrading,
  cartRows,
  cartTotal,
  cartSaved,
  onAdjust,
  onSave,
  onPrint,
}) {
  return (
    <aside className="grocery-cart">
      <h2>
        Cart
        {cartUpgrading ? (
          <span className="grocery-cart-syncing" aria-label="Syncing cart">
            &#8635;
          </span>
        ) : null}
      </h2>
      {cartRows.length === 0 ? <p>Your cart is empty.</p> : null}
      {cartRows.map((row) => (
        <div className="grocery-cart-row" key={row.item.id}>
          <div>
            <p>{row.item.name}</p>
            <small>₱{row.item.price.toFixed(2)} each</small>
          </div>
          <div className="grocery-cart-controls">
            <button type="button" onClick={() => onAdjust(row.item.id, -1)}>
              -
            </button>
            <span>{row.count}</span>
            <button type="button" onClick={() => onAdjust(row.item.id, 1)}>
              +
            </button>
          </div>
        </div>
      ))}
      <div className="grocery-total">
        <strong>Total</strong>
        <strong>₱{cartTotal.toFixed(2)}</strong>
      </div>
      {cartRows.length > 0 ? (
        <div className="grocery-cart-actions">
          <button type="button" className="grocery-save-btn" onClick={onSave}>
            {cartSaved ? "Saved!" : "Save cart"}
          </button>
          <button type="button" className="grocery-print-btn" onClick={onPrint}>
            Print cart
          </button>
        </div>
      ) : null}
    </aside>
  );
}

GroceryCartAside.propTypes = {
  cartUpgrading: PropTypes.bool.isRequired,
  cartRows: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        price: PropTypes.number,
      }),
      count: PropTypes.number,
      lineTotal: PropTypes.number,
    }),
  ).isRequired,
  cartTotal: PropTypes.number.isRequired,
  cartSaved: PropTypes.bool.isRequired,
  onAdjust: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
};

function GroceryPage() {
  const [allItems, setAllItems] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState({});
  const [user, setUser] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authFeedback, setAuthFeedback] = useState("");
  const [cartUpgrading, setCartUpgrading] = useState(false);
  const [cartSaved, setCartSaved] = useState(false);
  const saveTimerRef = useRef(null);
  const userRef = useRef(null);
  const cartRef = useRef({});

  // Keep cartRef in sync for use in unmount/async contexts
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim().toLowerCase());
    }, 120);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load cart from localStorage immediately — no waiting for auth
  useEffect(() => {
    const local = loadCart(null);
    Promise.resolve(local).then((saved) => {
      setCart(saved);
    });
  }, []);

  // Auth listener — upgrade cart from Firestore when signed in
  useEffect(() => {
    if (!hasFirebaseConfig || !auth) {
      return undefined;
    }

    const handleAuthChange = async (nextUser) => {
      userRef.current = nextUser;
      setUser(nextUser);

      if (nextUser) {
        // User signed in — fetch their Firestore cart (may be richer than local)
        setCartUpgrading(true);
        const saved = await loadCart(nextUser.uid);
        setCart(saved);
        setCartUpgrading(false);
      }
      // On sign-out, keep the current in-memory cart as-is
    };

    const unsubscribe = onAuthStateChanged(auth, handleAuthChange);
    return unsubscribe;
  }, []);

  // Mark as unsaved whenever cart changes
  useEffect(() => {
    setCartSaved(false);
  }, [cart]);

  // Debounced cart auto-save whenever cart changes (after initial load)
  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      const uid = userRef.current ? userRef.current.uid : null;
      if (uid) {
        saveCart(uid, cartRef.current);
      }
    }, 800);

    return undefined; // do NOT cancel on unmount — let it fire
  }, [cart]);

  // On unmount, flush any pending save immediately
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      const uid = userRef.current ? userRef.current.uid : null;
      saveCart(uid, cartRef.current);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchItems = async () => {
      setIsLoading(true);
      setError("");
      try {
        const items = await getGroceryItems();
        if (isMounted) {
          setAllItems(items);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            `Unable to load grocery items right now.${err instanceof Error ? " " + err.message : ""}`,
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchItems();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const withScore = allItems
      .map((item) => ({
        item,
        score: scoreItemAgainstQuery(item.name || "", searchQuery),
      }))
      .filter((entry) => entry.score > 0)
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        return left.item.name.localeCompare(right.item.name);
      });

    return withScore.map((entry) => entry.item);
  }, [allItems, searchQuery]);

  const cartRows = useMemo(
    () =>
      Object.entries(cart)
        .map(([itemId, count]) => {
          const item = allItems.find((entry) => entry.id === itemId);
          if (!item || count <= 0) {
            return null;
          }

          return {
            item,
            count,
            lineTotal: count * item.price,
          };
        })
        .filter(Boolean),
    [allItems, cart],
  );

  const cartTotal = useMemo(
    () => cartRows.reduce((sum, row) => sum + row.lineTotal, 0),
    [cartRows],
  );

  const handleGoogleLogin = async () => {
    if (!auth || !googleProvider) {
      setAuthFeedback("Firebase auth is not configured.");
      return;
    }

    setAuthFeedback("");
    try {
      setIsSigningIn(true);
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      const details = err instanceof Error ? ` (${err.message})` : "";
      setAuthFeedback(`Sign-in failed.${details}`);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleLogout = async () => {
    if (!auth) {
      return;
    }

    // Prevent logout if unsaved changes exist
    if (!cartSaved && Object.keys(cartRef.current).length > 0) {
      setAuthFeedback("Please save your cart before logging out.");
      return;
    }

    // Save cart to Firestore before signing out
    const uid = userRef.current ? userRef.current.uid : null;
    await saveCart(uid, cartRef.current);
    setAuthFeedback("");
    await signOut(auth);
    setCart({});
  };

  const handleAddToCart = (item) => {
    setCart((current) => ({
      ...current,
      [item.id]: (current[item.id] || 0) + 1,
    }));
  };

  const handleAdjustCart = (itemId, delta) => {
    setCart((current) => {
      const next = (current[itemId] || 0) + delta;
      if (next <= 0) {
        const copy = { ...current };
        delete copy[itemId];
        return copy;
      }

      return {
        ...current,
        [itemId]: next,
      };
    });
  };

  const handleSaveCart = async () => {
    const uid = userRef.current ? userRef.current.uid : null;
    if (!uid) {
      setAuthFeedback(
        "Please sign in first to save your cart to your account.",
      );
      return;
    }
    await saveCart(uid, cart);
    setCartSaved(true);
    setTimeout(() => setCartSaved(false), 2000);
  };

  const handlePrint = () => {
    // eslint-disable-next-line no-undef
    globalThis.print();
  };

  return (
    <main className="grocery-page">
      <section className="grocery-hero">
        <div className="grocery-hero-top">
          <div className="grocery-hero-copy">
            <p className="grocery-kicker">Smart Grocery</p>
            <h1>Find items quickly and add them to cart</h1>
            <p>
              Live fuzzy search updates while typing. Data source:{" "}
              {isCloudBacked() ? "Firebase" : "Local storage fallback"}.
            </p>
            <div className="grocery-actions">
              <Link className="grocery-link-button" to="/grocery/add-items">
                Add New Items
              </Link>
              <Link className="grocery-link-subtle" to="/grocery/stats">
                View Stats
              </Link>
              <Link className="grocery-link-subtle" to="/budget">
                Budget Planner
              </Link>
              <Link className="grocery-link-subtle" to="/">
                Back to Home
              </Link>
            </div>
          </div>

          {hasFirebaseConfig ? (
            <GroceryAuthPanel
              user={user}
              isSigningIn={isSigningIn}
              authFeedback={authFeedback}
              onLogin={handleGoogleLogin}
              onLogout={handleLogout}
              isLogoutDisabled={!cartSaved && Object.keys(cart).length > 0}
            />
          ) : null}
        </div>
      </section>

      <section className="grocery-panel">
        <label htmlFor="grocery-search">Search groceries</label>
        <input
          id="grocery-search"
          className="grocery-search"
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Try: apple, milk, choc"
        />
        <p className="grocery-meta">{filteredItems.length} matching item(s)</p>
      </section>

      {isLoading ? <p className="grocery-message">Loading items...</p> : null}
      {error ? <p className="grocery-message grocery-error">{error}</p> : null}

      {!isLoading && !error ? (
        <section className="grocery-layout">
          <div className="grocery-list">
            {filteredItems.length === 0 ? (
              <article className="grocery-empty">
                <h2>No items found</h2>
                <p>
                  Add some in /grocery/add-items and they will show up here.
                </p>
              </article>
            ) : (
              filteredItems.map((item) => (
                <article className="grocery-card" key={item.id}>
                  <div className="grocery-image-wrap">
                    {item.image ? (
                      <img src={item.image} alt={item.name} />
                    ) : (
                      <div className="grocery-placeholder">No image</div>
                    )}
                  </div>
                  <div className="grocery-card-body">
                    <h2>{item.name}</h2>
                    <p>In stock: {item.quantity}</p>
                    <p className="grocery-price">₱{item.price.toFixed(2)}</p>
                    <button
                      type="button"
                      className="grocery-add-btn"
                      onClick={() => handleAddToCart(item)}
                      disabled={item.quantity <= 0}
                    >
                      {item.quantity > 0 ? "Add to cart" : "Out of stock"}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>

          <GroceryCartAside
            cartUpgrading={cartUpgrading}
            cartRows={cartRows}
            cartTotal={cartTotal}
            cartSaved={cartSaved}
            onAdjust={handleAdjustCart}
            onSave={handleSaveCart}
            onPrint={handlePrint}
          />

          {/* Hidden print template — only visible when printing */}
          <div className="grocery-print-template" aria-hidden="true">
            <header className="grocery-print-header">
              <h1>Grocery Order</h1>
              <p className="grocery-print-date">
                {new Date().toLocaleDateString("en-PH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </header>

            <table className="grocery-print-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {cartRows.map((row) => (
                  <tr key={row.item.id}>
                    <td>{row.item.name}</td>
                    <td>{row.count}</td>
                    <td>₱{row.item.price.toFixed(2)}</td>
                    <td>₱{row.lineTotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3">
                    <strong>Grand Total</strong>
                  </td>
                  <td>
                    <strong>₱{cartTotal.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>

            <footer className="grocery-print-footer">
              <p>kydrawss.art</p>
            </footer>
          </div>
        </section>
      ) : null}
    </main>
  );
}

export default GroceryPage;
